import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import LinkedInLogin from '../components/LinkedInLogin';
import api from '../services/api';

// Google Client ID - Configurado para Guayerd
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '362187424609-dft8ld3ft7e1ngk9ok36ci9lgoua9ohm.apps.googleusercontent.com';

const Login = () => {
  const navigate = useNavigate();
  const { loginWithKey, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoutReason, setLogoutReason] = useState(null);
  const [linkedInProcessing, setLinkedInProcessing] = useState(false);
  
  // Form data
  const [accessKey, setAccessKey] = useState('');
  const accessKeyRef = useRef(null);
  
  // Verificar razón de logout al cargar
  useEffect(() => {
    const reason = sessionStorage.getItem('logoutReason');
    if (reason) {
      setLogoutReason(reason);
      sessionStorage.removeItem('logoutReason');
    }
  }, []);
  
  // Redirigir si ya está autenticado
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Cuando hay error, volver el foco al campo de access code
  useEffect(() => {
    if (error && accessKeyRef.current) {
      // Usar setTimeout para asegurar que el estado se actualice antes del focus
      setTimeout(() => {
        accessKeyRef.current?.focus();
      }, 100);
    }
  }, [error]);

  const handleKeySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await loginWithKey(accessKey);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
      // Limpiar el campo y volver el foco cuando hay error
      setAccessKey('');
      setLoading(false);
      // Usar setTimeout para asegurar que el estado se actualice antes del focus
      setTimeout(() => {
        accessKeyRef.current?.focus();
      }, 100);
      return;
    }
    
    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('Google login attempt started');
    setError('');
    setLoading(true);
    
    try {
      // Verificar que tenemos el credential
      if (!credentialResponse?.credential) {
        throw new Error('No se recibió el token de Google');
      }
      
      // Decode the Google token to get user information
      const googleToken = credentialResponse.credential;
      // Decodificar correctamente con soporte UTF-8
      const base64Url = googleToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const googlePayload = JSON.parse(jsonPayload);
      console.log('Google user info:', googlePayload);
      
      console.log('Sending token to backend...');
      // Enviar el token de Google al backend
      const response = await api.post('/api/v1/auth/google-login', {
        token: credentialResponse.credential
      });
      
      console.log('Backend response:', response.data);
      
      if (response.data.access_token) {
        // Guardar el token en localStorage
        localStorage.setItem('token', response.data.access_token);
        
        // Decode the JWT to get user info
        const base64Url = response.data.access_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const userInfo = JSON.parse(jsonPayload);
        
        console.log('User info:', userInfo);
        
        // Save complete Google user information
        const userProfile = {
          email: googlePayload.email,
          name: googlePayload.name,
          picture: googlePayload.picture,
          given_name: googlePayload.given_name,
          family_name: googlePayload.family_name,
          locale: googlePayload.locale || 'es',
          provider: 'google',
          login_method: 'Google'
        };
        
        console.log('Saving user profile:', userProfile);
        console.log('Profile picture URL:', googlePayload.picture);
        
        // Set the user in localStorage
        localStorage.setItem('user', userInfo.sub || userInfo.email);
        localStorage.setItem('userProfile', JSON.stringify(userProfile));
        
        // Guardar el email para futuros logins (mejora UX)
        if (userInfo.email) {
          localStorage.setItem('lastEmail', userInfo.email);
        }
        
        // Forzar un reload para que el AuthContext detecte el nuevo token
        // y redirija automáticamente al dashboard
        window.location.href = '/dashboard';
      } else {
        throw new Error('No se recibió token del servidor');
      }
    } catch (err) {
      console.error('Google login error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        fullError: err
      });
      
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Error signing in with Google. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const handleGoogleError = () => {
    setError('Error signing in with Google');
  };

  const handleLinkedInSuccess = async (code) => {
    console.log('LinkedIn login attempt started');
    
    // Evitar procesamiento doble del código (React StrictMode puede causar esto)
    if (linkedInProcessing) {
      console.log('Already processing LinkedIn login, skipping duplicate call');
      return;
    }
    
    setLinkedInProcessing(true);
    setError('');
    setLoading(true);
    
    try {
      // Enviar el código al backend para intercambiarlo por un token
      const response = await api.post('/api/v1/auth/linkedin-login', {
        code: code,
        redirect_uri: `${window.location.origin}/linkedin`
      });
      
      if (response.data.access_token) {
        // Guardar el token
        localStorage.setItem('token', response.data.access_token);
        
        // Decode the token to get user info
        const base64Url = response.data.access_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64).split('').map(c => 
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          ).join('')
        );
        const userInfo = JSON.parse(jsonPayload);
        
        console.log('LinkedIn login successful for:', userInfo.sub);
        
        // Guardar el perfil completo de LinkedIn
        const linkedInProfile = {
          email: userInfo.sub,
          name: userInfo.name || `${userInfo.first_name} ${userInfo.last_name}`.trim() || userInfo.sub,
          picture: userInfo.picture,
          provider: 'linkedin',
          title: userInfo.title || 'LinkedIn Professional',
          login_method: userInfo.login_method || 'LinkedIn'
        };
        
        localStorage.setItem('userProfile', JSON.stringify(linkedInProfile));
        
        // Configurar el token en axios
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        
        // Forzar reload para que el AuthContext detecte el nuevo token
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('LinkedIn login error:', error);
      setError(error.response?.data?.detail || 'Error al autenticar con LinkedIn');
      // Reset el flag después de un error para permitir reintentar
      setTimeout(() => setLinkedInProcessing(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInError = (error) => {
    console.error('LinkedIn OAuth error:', error);
    setError('Error connecting with LinkedIn. Please try again.');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0C279F 0%, #00CDFF 100%)',
        position: 'relative',
      }}
    >
      {/* Geometric shapes background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00F0B1 0%, #00CDFF 100%)',
            opacity: 0.1,
            top: '-200px',
            right: '-200px',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0C279F 0%, #00F0B1 100%)',
            opacity: 0.1,
            bottom: '-150px',
            left: '-150px',
          }}
        />
      </Box>

      <Container component="main" maxWidth="xs" sx={{ zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'white',
            borderRadius: 3,
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <img 
              src="/assets/branding/guayerd-isologo.svg"
              alt="GuayerdOS" 
              style={{ width: '48px', height: '48px' }} 
            />
          </Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            GuayerdOS
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: 4,
              fontSize: '0.9rem',
            }}
          >
            Educational Empowerment Platform
          </Typography>

          {/* Contextual session expired message */}
          {logoutReason && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 3, 
                width: '100%',
                borderRadius: 2,
                backgroundColor: '#fff3e0',
                color: '#e65100',
                '& .MuiAlert-icon': {
                  color: '#ff9800'
                }
              }}
              onClose={() => setLogoutReason(null)}
            >
              {logoutReason === 'expired' && 'Your session has expired. Please sign in again.'}
              {logoutReason === 'inactivity' && 'Your session was closed due to inactivity. Please sign in again.'}
              {logoutReason === 'invalid' && 'Your session is invalid. Please sign in again.'}
              {logoutReason === 'concurrent' && 'Another active session was detected. Please sign in again.'}
            </Alert>
          )}

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                width: '100%',
                borderRadius: 2,
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <Box 
            component="form" 
            onSubmit={handleKeySubmit} 
            sx={{ width: '100%' }}
            noValidate
            autoComplete="off"
          >
            <TextField
              margin="normal"
              required
              fullWidth
              label="Access Code"
              name="accessKey"
              type={showPassword ? 'text' : 'password'}
              autoFocus
              autoComplete="new-password"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleKeySubmit(e);
                }
              }}
              disabled={loading}
              inputRef={accessKeyRef}
              inputProps={{
                autoComplete: 'new-password',
                form: {
                  autoComplete: 'off',
                }
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                  '&:hover fieldset': {
                    borderColor: '#00CDFF',
                    borderWidth: 2,
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0C279F',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0C279F',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      tabIndex={-1}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.54)',
                        '&:hover': {
                          color: '#0C279F',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.75,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: loading 
                  ? '#ccc'
                  : '#0C279F', // Azul pleno Guayerd
                color: 'white',
                boxShadow: loading 
                  ? 'none'
                  : '0 4px 20px rgba(12, 39, 159, 0.25)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: loading 
                    ? '#ccc'
                    : '#0a1f7a', // Azul más oscuro al hover
                  boxShadow: loading 
                    ? 'none'
                    : '0 6px 25px rgba(12, 39, 159, 0.35)',
                  transform: loading ? 'none' : 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  backgroundColor: '#081661',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#ccc',
                  color: 'rgba(0, 0, 0, 0.38)',
                },
              }}
            >
              {loading ? 'Authenticating...' : 'Enter with Access Key'}
            </Button>
          </Box>

          {/* Divider */}
          <Box sx={{ width: '100%', my: 3, position: 'relative' }}>
            <Divider>
              <Typography variant="body2" sx={{ color: '#999', px: 2 }}>
                O
              </Typography>
            </Divider>
          </Box>
          
          {/* Social Login Buttons - Improved Design */}
          <Box sx={{ width: '100%' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                color: '#666', 
                mb: 2,
                fontSize: '0.85rem'
              }}
            >
              Or sign in with your social account
            </Typography>
            
            {/* Social Buttons Container */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              width: '100%'
            }}>
              {/* Google Login Button - Custom Wrapper */}
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  width: '100%',
                  position: 'relative',
                  '& > div': {
                    width: '100% !important',
                  },
                  '& iframe': {
                    width: '100% !important',
                    maxWidth: '100% !important',
                    display: 'block',
                    minHeight: '44px',
                  }
                }}>
                  <Box sx={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #dadce0',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Avoid clipping/empty state of GSI iframe
                    overflow: 'visible',
                    lineHeight: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(66, 133, 244, 0.04)',
                      borderColor: '#4285f4',
                      boxShadow: '0 1px 3px 0 rgba(66, 133, 244, 0.3)',
                    }
                  }}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      theme="outline"
                      size="large"
                      width={280}
                      text="continue_with"
                      shape="rectangular"
                      locale="en"
                      auto_select={true}
                    />
                  </Box>
                </Box>
              </GoogleOAuthProvider>
              
              {/* LinkedIn Login Button - Enhanced */}
              <Box sx={{ width: '100%' }}>
                <LinkedInLogin 
                  onSuccess={handleLinkedInSuccess}
                  onError={handleLinkedInError}
                  disabled={loading}
                />
              </Box>
            </Box>
            
            {/* Security note removed per request */}
          </Box>

          <Typography
            variant="caption"
            sx={{
              mt: 4,
              color: '#999',
              textAlign: 'center',
              display: 'block',
              fontSize: '0.75rem',
            }}
          >
            Guayerd Latam © 2019 - ∞
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;